import { Injectable } from '@angular/core';
import { Subject, Observable, Subscription } from 'rxjs';
import { DeviceService } from './device.service';
import { WebPubSubClient } from '@azure/web-pubsub-client';

interface PubSubMessage {
  sourceId: string;
  sourceType: 'device' | 'floor';
  data: any;
}

interface ClientEntry {
  client: WebPubSubClient;
  sourceId: string;
  type: 'device' | 'floor';
  generation: number;
}

@Injectable({
  providedIn: 'root'
})
export class PubSubService {

  private clientMap = new Map<string, ClientEntry>();
  private pendingHttp = new Map<string, Subscription>();
  private generationMap = new Map<string, number>();
  private messageSubject = new Subject<PubSubMessage>();

  constructor(private deviceService: DeviceService) {}

  get messages$(): Observable<PubSubMessage> {
    return this.messageSubject.asObservable();
  }

  connect(sourceId: string, type: 'device' | 'floor' = 'device'): void {
    if (!sourceId) {
      return;
    }

    const key = this.key(sourceId, type);

    this.disconnectType(type === 'device' ? 'floor' : 'device');
    this.disconnectOthersOfType(type, sourceId);

    if (this.clientMap.has(key)) {
      console.warn(`Already connected: ${key}`);
      return;
    }

    const generation = this.bumpGeneration(key);
    this.cancelPending(key);

    const sub = this.deviceService.getPubSubUrl(sourceId, type).subscribe({
      next: (res: any) => {
        this.pendingHttp.delete(key);

        if (!this.isCurrent(key, generation)) {
          return;
        }

        this.startClient(key, sourceId, type, res?.url, generation);
      },
      error: (err) => {
        this.pendingHttp.delete(key);
        console.error('❌ PubSub URL error:', err);
      }
    });

    this.pendingHttp.set(key, sub);
  }

  disconnect(sourceId?: string, type?: 'device' | 'floor'): void {
    if (!sourceId) {
      this.disconnectAll();
      return;
    }

    this.disconnectKey(this.key(sourceId, type || 'device'));
  }

  private key(sourceId: string, type: 'device' | 'floor'): string {
    return `${type}-${sourceId}`;
  }

  private bumpGeneration(key: string): number {
    const next = (this.generationMap.get(key) ?? 0) + 1;
    this.generationMap.set(key, next);
    return next;
  }

  private isCurrent(key: string, generation: number): boolean {
    return this.generationMap.get(key) === generation;
  }

  private disconnectAll(): void {
    const keys = new Set<string>([
      ...this.clientMap.keys(),
      ...this.pendingHttp.keys(),
      ...this.generationMap.keys()
    ]);

    keys.forEach((key) => this.disconnectKey(key));
  }

  private disconnectType(type: 'device' | 'floor'): void {
    const prefix = `${type}-`;
    const keys = new Set<string>([
      ...this.clientMap.keys(),
      ...this.pendingHttp.keys()
    ]);

    keys.forEach((key) => {
      if (key.startsWith(prefix)) {
        this.disconnectKey(key);
      }
    });
  }

  private disconnectOthersOfType(
    type: 'device' | 'floor',
    keepSourceId: string
  ): void {
    const keepKey = this.key(keepSourceId, type);
    const prefix = `${type}-`;
    const keys = new Set<string>([
      ...this.clientMap.keys(),
      ...this.pendingHttp.keys()
    ]);

    keys.forEach((key) => {
      if (key.startsWith(prefix) && key !== keepKey) {
        this.disconnectKey(key);
      }
    });
  }

  private disconnectKey(key: string): void {
    this.bumpGeneration(key);
    this.cancelPending(key);
    this.stopAndRemove(key);
  }

  private cancelPending(key: string): void {
    const sub = this.pendingHttp.get(key);
    if (sub) {
      sub.unsubscribe();
      this.pendingHttp.delete(key);
    }
  }

  private startClient(
    key: string,
    sourceId: string,
    type: 'device' | 'floor',
    url: string,
    generation: number
  ): void {
    if (!url || !this.isCurrent(key, generation)) {
      return;
    }

    const client = new WebPubSubClient(url);

    const emitIfCurrent = (data: any) => {
      if (!this.isCurrent(key, generation) || !this.clientMap.has(key)) {
        return;
      }

      if (!data) {
        return;
      }

      this.messageSubject.next({
        sourceId,
        sourceType: type,
        data
      });
    };

    client.on('connected', () => {
      console.log('✅ Connected to WebPubSub');
    });

    client.on('disconnected', (e) => {
      console.warn('🔴 Disconnected:', e.message);
    });

    client.on('group-message', (e: any) => {
      emitIfCurrent(e?.message?.data);
    });

    client.on('server-message', (e: any) => {
      const data = e?.message?.data;
      if (!data) {
        return;
      }
      emitIfCurrent(data);
    });

    this.clientMap.set(key, { client, sourceId, type, generation });

    client.start().then(async () => {
      if (!this.isCurrent(key, generation)) {
        await this.safeStop(client, type, sourceId);
        this.removeIfGeneration(key, generation);
        return;
      }

      console.log(`✅ CONNECTED: ${key}`);

      if (type === 'floor') {
        await client.joinGroup(`floor-${sourceId}`);

        if (!this.isCurrent(key, generation)) {
          await this.safeStop(client, type, sourceId);
          this.removeIfGeneration(key, generation);
        }
      }
    }).catch((err) => {
      console.error('❌ Connection error:', err);
      this.removeIfGeneration(key, generation);
    });
  }

  private stopAndRemove(key: string): void {
    const entry = this.clientMap.get(key);
    this.clientMap.delete(key);

    if (!entry) {
      return;
    }

    void this.safeStop(entry.client, entry.type, entry.sourceId);
  }

  private removeIfGeneration(key: string, generation: number): void {
    const entry = this.clientMap.get(key);
    if (entry?.generation === generation) {
      this.clientMap.delete(key);
    }
  }

  private async safeStop(
    client: WebPubSubClient,
    type: 'device' | 'floor',
    sourceId: string
  ): Promise<void> {
    try {
      if (type === 'floor') {
        await client.leaveGroup(`floor-${sourceId}`);
      }
    } catch {
      // Socket may already be closed
    }

    try {
      await client.stop();
    } catch {
      // Ignore teardown errors
    }
  }
}
